// src/api/user.ts
//
// Typed client for the user service (profile, auth) and Stripe billing.

import { getAuthUrl, getUserServiceUrl } from '../utils/backend'
import { createApiClient } from './base'

const userClient = createApiClient(getUserServiceUrl)
const authClient = createApiClient(getAuthUrl)

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type UserProfile = {
  id: string
  email: string
  first_name?: string
  last_name?: string
  cell?: string
  timezone?: string
}

export type BillingStatus = {
  billingExempt?: boolean
  subscriptionStatus?: string | null
}

export type StripePrice = {
  id: string
  unit_amount: number
  currency: string
  recurring?: { interval: 'month' | 'year'; interval_count: number }
}

// ---------------------------------------------------------------------------
// User profile
// ---------------------------------------------------------------------------

export async function getUser(userId: string, signal?: AbortSignal): Promise<UserProfile> {
  return userClient.request(`/user/?id=${encodeURIComponent(userId)}`, { signal })
}

export async function getUserByEmail(
  email: string,
  signal?: AbortSignal,
): Promise<UserProfile | null> {
  try {
    return await userClient.request(`/user/?email=${encodeURIComponent(email)}`, { signal })
  } catch {
    return null
  }
}

export type UpdateUserPayload = {
  id: string
  email: string
  cell?: string
  first_name: string
  last_name: string
  timezone?: string
}

export async function updateUser(payload: UpdateUserPayload): Promise<void> {
  await userClient.request('/user/update/', { method: 'POST', body: payload })
}

export type CreateUserPayload = {
  email: string
  firstName: string
  lastName: string
  cell: string
  pin: string
  timezone: string
}

export async function createUser(
  payload: CreateUserPayload,
  authToken?: string,
): Promise<void> {
  const extraHeaders = authToken ? { Authorization: `Bearer ${authToken}` } : undefined
  await userClient.request('/user/create/', {
    method: 'POST',
    body: payload,
    headers: extraHeaders,
  })
}

// ---------------------------------------------------------------------------
// Auth service (PIN, phone verification)
// ---------------------------------------------------------------------------

/** Update the user's PIN. Returns the server's text response. */
export async function updatePin(userId: string, pin: string): Promise<string> {
  const result = await userClient.request<string | null>('/auth/pin/update/', {
    method: 'POST',
    body: { userId, pin },
  })
  return result ?? ''
}

export async function requestPhoneVerification(
  cell: string,
): Promise<{ sent: boolean; expires_at?: string }> {
  return authClient.request('/auth/phone/request/', {
    method: 'POST',
    body: { cell },
  })
}

export async function verifyPhone(
  cell: string,
  code: string,
): Promise<{ verified: boolean }> {
  return authClient.request('/auth/phone/verify/', {
    method: 'POST',
    body: { cell, code },
  })
}

/** Complete an OAuth callback (Google or Azure). */
export async function completeOAuthCallback(
  provider: 'google' | 'azure',
  body: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  const endpoint = provider === 'azure' ? '/auth/azure/callback/' : '/auth/google/callback/'
  return authClient.request(endpoint, {
    method: 'POST',
    body,
  })
}

/** Initiate a Scribbl-native signup (checks email availability). */
export async function initScribblSignup(email: string): Promise<void> {
  await authClient.request('/scribbl/signup/init/', {
    method: 'POST',
    body: { email },
  })
}

export type ConfirmScribblSignupPayload = {
  email: string
  password: string
  firstName: string
  lastName: string
  phone: string
  pin: string
  timezone?: string
  source?: string
}

export type ConfirmScribblSignupResponse = {
  email?: string
  userId?: string
  user_id?: string
  token?: string
  session_token?: string
  expiresAt?: string | number
  expires_at?: string | number
  firstName?: string
  first_name?: string
  lastName?: string
  last_name?: string
}

/** Confirm a Scribbl-native signup. Returns the server JSON response. */
export async function confirmScribblSignup(
  payload: ConfirmScribblSignupPayload,
): Promise<ConfirmScribblSignupResponse> {
  return authClient.request('/scribbl/signup/confirm/', {
    method: 'POST',
    body: payload,
  })
}

export type ScribblLoginPayload = {
  username: string
  password: string
}

/** Exchange Scribbl credentials for a session token. */
export async function scribblLogin(
  payload: ScribblLoginPayload,
): Promise<Record<string, unknown>> {
  return authClient.request('/auth/login', {
    method: 'POST',
    body: { email: payload.username, password: payload.password },
  })
}

export async function requestMagicLink(email: string): Promise<Record<string, unknown>> {
  return authClient.request('/auth/magic-link', {
    method: 'POST',
    body: { email },
  })
}

export async function beginPasskeyLogin(email?: string): Promise<Record<string, unknown>> {
  return authClient.request('/auth/passkeys/login/options', {
    method: 'POST',
    body: email ? { email } : {},
  })
}

export async function finishPasskeyLogin(credential: unknown): Promise<Record<string, unknown>> {
  return authClient.request('/auth/passkeys/login', {
    method: 'POST',
    body: { credential },
  })
}

/** Exchange Apple identity token for a session token. */
export async function appleLogin(
  idToken: string,
): Promise<Record<string, unknown>> {
  return authClient.request('/auth/apple/callback/', {
    method: 'POST',
    body: { id_token: idToken },
  })
}

export type ForgotPasswordRequestPayload = {
  email: string
  verify: 'email' | 'cell'
}

/** Request a forgot-password reset code. */
export async function requestPasswordReset(
  payload: ForgotPasswordRequestPayload,
): Promise<Record<string, unknown>> {
  return authClient.request('/scribbl/signup/forgot-password/request/', {
    method: 'POST',
    body: payload,
  })
}

export type ForgotPasswordConfirmPayload = {
  email: string
  verify: 'email' | 'cell'
  code: string
  password: string
}

/** Confirm a forgot-password reset with code and new password. */
export async function confirmPasswordReset(
  payload: ForgotPasswordConfirmPayload,
): Promise<void> {
  await authClient.request('/scribbl/signup/forgot-password/confirm/', {
    method: 'POST',
    body: payload,
  })
}

// ---------------------------------------------------------------------------
// Stripe / billing
// ---------------------------------------------------------------------------

export async function getBillingStatus(userId: string): Promise<BillingStatus> {
  return userClient.request(
    `/stripe/billing/status/?user_id=${encodeURIComponent(userId)}`,
  )
}

export async function cancelSubscription(
  userId: string,
  immediately = false,
): Promise<BillingStatus> {
  const params = new URLSearchParams({ user_id: userId })
  if (immediately) params.set('immediately', 'true')
  return userClient.request(`/stripe/subscription/cancel/?${params.toString()}`, {
    method: 'POST',
  })
}

export async function getStripePrices(
  productId: string,
  userId?: string,
): Promise<StripePrice[]> {
  const params = new URLSearchParams({ product_id: productId })
  if (userId) params.set('user_id', userId)
  return userClient.request(`/stripe/prices/?${params.toString()}`)
}

export type CreateCheckoutSessionPayload = {
  user_id: string
  price_id: string
  success_url: string
  cancel_url: string
}

export async function createCheckoutSession(
  payload: CreateCheckoutSessionPayload,
): Promise<{ checkoutUrl?: string | null; checkout_url?: string | null }> {
  return userClient.request('/stripe/checkout/session/', {
    method: 'POST',
    body: payload,
  })
}
